<template>
  <div v-if="linkedDocuments.length || editing">
    <div class="spacer" />
    <hr>
  </div>
  <v-container
    v-if="linkedDocuments.length > 0 || isLinkingDocuments"
    data-testid="linked-documents-container"
    class="pa-3"
  >
    <v-row v-if="linkedDocuments.length > 0">
      <v-expansion-panels v-model="containerPanel">
        <!-- Linked Document Container Panel -->
        <v-expansion-panel elevation="0">
          <v-expansion-panel-title class="subheading">
            {{ sectionTitle }}
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <!-- Expansion Panel for Each Document -->
            <v-expansion-panels>
              <v-expansion-panel
                v-for="doc in linkedDocuments"
                :key="doc.id"
                class="comp-panel"
                elevation="0"
              >
                <v-expansion-panel-title>
                  <template #default="{ expanded }">
                    <CaretIcon
                      class="caret"
                      :data-testid="`comp-group-caret-icon-${doc.id}`"
                      :class="{ expanded }"
                    />
                    <div
                      class="section"
                      @click.stop="handleLinkedDocumentClick(doc)"
                    >
                      <v-avatar
                        v-if="doc.iconUrl || iconUrl"
                        size="23"
                        class="linked-doc-avatar section__left"
                      >
                        <img
                          :src="doc.iconUrl || iconUrl"
                          class="add-link-search__item-icon"
                        >
                      </v-avatar>
                      <component
                        :is="config.isOmInfoWindow ? 'span' : 'a'"
                        class="display-field-text section__left"
                        :data-testid="`linked-documents-header-${doc.id}`"
                        :class="{'section__expanded': expanded, 'section__external-link': config.linkAction}"
                        :style="{ color: doc.textColor ? doc.textColor : ''}"
                      >
                        {{ doc.name }}
                      </component>
                      <span
                        v-if="!expanded"
                        data-testid="linked-documents-header-suffix"
                        class="display-field-text section__right"
                        :style="{ color: doc.textColor ? doc.textColor : ''}"
                      >
                        {{ doc.headerSuffix }}
                      </span>
                    </div>
                    <TrashIcon
                      v-if="editing && !doc.isLinkedOm"
                      class="delete-button"
                      :data-testid="`linked-documents-delete-icon-${doc.id}`"
                      @click="handleDeleteLinkedDocument(doc.id)"
                    />
                  </template>
                </v-expansion-panel-title>
                <v-expansion-panel-text eager>
                  <DisplayFields
                    :display-fields="doc.displayFields"
                    :data-testid="`linked-documents-display-fields-${doc.id}`"
                    class="comp-groups-display-fields"
                  />
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-row>
    <v-row v-if="isLinkingDocuments">
      <AlgoliaAutocomplete
        data-testid="linked-search-bar"
        :placeholder-text="placeholderText"
        :icon-url="iconUrl"
        :get-item-icon-url="getHitIconUrl"
        :algolia-index-name="algoliaIndexName"
        :algolia-hits="linkedDocuments"
        @select="onLinkSelect"
        @stop-searching="$emit('stop-linking')"
      />
    </v-row>
  </v-container>
</template>

<script lang="ts">
import CaretIcon from '@/components/svgs/CaretIcon.vue';
import DisplayFields from '../DisplayFields.vue';
import AlgoliaAutocomplete from './AlgoliaAutocomplete.vue';
import TrashIcon from '@/components/svgs/TrashIcon.vue';

import {DisplayField, InfoWindowContent} from '@/lib/classes/InfoWindowContent';
import {currentUser, getDocument, queryCollectionByIds, updateDocument} from '@/services/FirestoreService';
import {getCollection} from '@/lib/filters/filters';
import {markerIconMap} from '@/services/MapService/Icons';
import type {LinkingConfig} from './Linking';
import {DisplayFieldOptions} from '@/lib/classes/InfoWindowContent/DisplayField';
import {NEW_BUILDING_ID} from '@/lib/symbols';

import {computed, PropType} from 'vue';
import {mapActions, mapMutations, mapState} from 'vuex';
import {InternalAppState} from '@/internal/store';
import {getItemFromCollection} from '@/services/OMService';
import {LightOMItem} from '@/services/OMService/types';

interface LinkedDocumentToDisplay {
  id: string;
  collection: string;
  name: string;
  headerSuffix: string | null;
  displayFields: DisplayField[];
  isLinkedOm: boolean;
  textColor?: string | null;
  iconUrl?: string;
}

export default {
  name: 'LinkedDocuments',
  components: {
    CaretIcon,
    DisplayFields,
    AlgoliaAutocomplete,
    TrashIcon,
  },
  provide() {
    return {
      leftMargin: computed(() => this.editing ? '20px' : '14px'),
    };
  },
  props: {
    sectionTitle: {
      type: String,
      required: true,
    },
    editing: {
      type: Boolean,
      required: true,
    },
    isLinkingDocuments: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object as PropType<LinkingConfig>,
      required: true,
    },
    infoWindowContent: {
      type: InfoWindowContent,
      required: true,
    },
    iconUrl: {
      type: String,
      required: false,
      default: '',
    },
    /* Determines if the link will be shown on the item to link to
     * OMs are hidden by default, and are only shown after they are approved.
     */
    hiddenLink: {
      type: Boolean,
      default: false,
    },
    placeholderText: {
      type: String,
      default: 'Enter Name',
    },
  },
  emits: ['stop-linking'],
  data() {
    const useProdGCP = process.env.VUE_APP_GCP_ENV === 'production';
    const algoliaIndexName = useProdGCP ? `prod_${this.config.firestoreCollection}` : `staging_${this.config.firestoreCollection}`;
    return {
      containerPanel: 0,
      localContent: this.infoWindowContent,
      linkedDocuments: [] as LinkedDocumentToDisplay[],
      algoliaIndexName,
    };
  },
  computed: {
    ...mapState('infowindow', ['infoWindowContents']),
    ...mapState({
      omCompetitiveBuildings: (state: any): InternalAppState['omCompetitiveBuildings'] => (state as InternalAppState).omCompetitiveBuildings,
      omCompetitivePortfolios: (state: any): InternalAppState['omCompetitivePortfolios'] => (state as InternalAppState).omCompetitivePortfolios,
      bulkCompetitiveBuildings: (state: any): InternalAppState['bulkCompetitiveBuildings'] => (state as InternalAppState).bulkCompetitiveBuildings,
      bulkCompetitivePortfolios: (state: any): InternalAppState['bulkCompetitivePortfolios'] => (state as InternalAppState).bulkCompetitivePortfolios,
    }),
  },
  mounted() {
    this.collectLinkedDocuments();
  },
  methods: {
    ...mapMutations(['setSnack']),
    ...mapMutations('infowindow', ['setSelectedTabIndex']),
    ...mapActions('infowindow', ['closeInfoWindowTab']),
    getHitIconUrl(hit: {objectID?: string}): string | undefined {
      const record = hit.objectID
        ? getCollection(this.config.filterCollection).getItem(hit.objectID)
        : null;
      const iconName = record?.markerData?.icon;
      return iconName ? markerIconMap.get(iconName)?.url : undefined;
    },
    retrieveIWItem() {
      if (!this.localContent.host) return null;
      if (!this.localContent.host!.filterCollection) {
        // we don't have filter collections for building and portfolio oms, instead we have arrays in the store.
        // if the filter collection doesn't exist on the host, we know to get the data from the store arrays.
        type StoreCollection =
          | 'omCompetitiveBuildings'
          | 'omCompetitivePortfolios'
          | 'bulkCompetitiveBuildings'
          | 'bulkCompetitivePortfolios';
        const localCollection: StoreCollection = this.localContent.host!.localCollection as StoreCollection;
        return this[localCollection].find((om: LightOMItem) => om.id === this.localContent.id);
      }
      const iwItemCollection = getCollection(this.localContent.host!.filterCollection!);
      return iwItemCollection.getItem(this.localContent.id as string);
    },
    /**
     * Handles the selection of a linked document from the AlgoliaAutocomplete component
     */
    async onLinkSelect(selectedItem: Partial<{objectID: string; Name: string}>) {
      const id = selectedItem.objectID;
      if (!id) return;

      const user = currentUser();
      if (!user) {
        console.error('No current user, cannot link document');
        return;
      }

      const itemToLinkCollection = getCollection(this.config.filterCollection);
      const itemToLink = itemToLinkCollection.getItem(id);

      if (!itemToLink || itemToLink.deleted) {
        console.warn(`Item with ID "${id}" does not exist or is deleted.`);
        return;
      }

      if (!itemToLink.linkedDocuments || !Array.isArray(itemToLink.linkedDocuments)) {
        itemToLink.linkedDocuments = [];
      }

      // Prevent duplicate linking
      if (itemToLink.linkedDocuments.some((doc: {id: string; collection: string}) => doc.id === this.localContent.id)) {
        console.warn(`Item with ID "${id}" is already linked.`);
        return;
      }

      const iwItem = this.retrieveIWItem();

      if (!iwItem) {
        console.error('The Info Window host item could not be found.');
        return;
      }

      if (!iwItem.linkedDocuments || !Array.isArray(iwItem.linkedDocuments)) {
        iwItem.linkedDocuments = [];
      }

      iwItem.linkedDocuments.push({collection: this.config.firestoreCollection, id});

      const updatedHostItem = {
        ...iwItem,
        editedOn: {
          timestamp: new Date(),
          name: user.displayName,
          uid: user.uid,
        },
      };

      const iwItemCollection = getCollection(this.localContent.host!.firestoreCollection!);
      if (iwItemCollection) {
        iwItemCollection.addItems(updatedHostItem);
      }

      itemToLink.linkedDocuments.push({
        collection: this.localContent.host?.firestoreCollection,
        id: this.localContent.id as string,
      });

      // Only update the editedOn field if the link is not hidden
      // This is to prevent overwriting the editedOn field when linking documents that are not yet visible
      // in the Info Window.
      const updatedItemToLink = {
        ...itemToLink,
        ...(this.hiddenLink
          ? {}
          : {
              editedOn: {
                timestamp: new Date(),
                name: user.displayName,
                uid: user.uid,
              },
            }),
      };

      getCollection(this.config.filterCollection).addItems(updatedItemToLink);

      this.collectLinkedDocuments();

      await Promise.all([
        updateDocument(this.localContent.host!.firestoreCollection!, this.localContent.id as string, {
          linkedDocuments: iwItem.linkedDocuments,
          editedOn: updatedHostItem.editedOn,
        }),
        updateDocument(
          this.config.firestoreCollection,
          id,
          this.hiddenLink
            ? {linkedDocuments: itemToLink.linkedDocuments}
            : {
                linkedDocuments: itemToLink.linkedDocuments,
                editedOn: updatedItemToLink.editedOn,
              },
        ),
      ]);
    },
    async handleDeleteLinkedDocument(id: string) {
      this.linkedDocuments = this.linkedDocuments.filter((doc) => doc.id !== id);
      const iwItem = this.retrieveIWItem();

      if (!iwItem) {
        console.error('The Info Window host item could not be found.');
        return;
      }

      iwItem.linkedDocuments = iwItem.linkedDocuments.filter((doc: {id: string}) => doc.id !== id);
      const updatedHostItem = {
        ...iwItem,
        editedOn: {
          timestamp: new Date(),
          name: currentUser()!.displayName,
          uid: currentUser()!.uid,
        },
      };

      const iwItemCollection = getCollection(this.localContent.host!.firestoreCollection!);
      if (iwItemCollection) {
        iwItemCollection.addItems(updatedHostItem);
      }

      const itemToUnlinkCollection = getCollection(this.config.filterCollection);
      const itemToUnlink = itemToUnlinkCollection.getItem(id);

      if (!itemToUnlink || itemToUnlink.deleted) {
        console.warn(`Item with ID "${id}" does not exist or is deleted.`);
        return;
      }

      itemToUnlink.linkedDocuments = itemToUnlink.linkedDocuments
        .filter((doc: {id: string}) => doc.id !== this.localContent.id);

      const updatedItemToUnlink = {
        ...itemToUnlink,
        editedOn: {
          timestamp: new Date(),
          name: currentUser()!.displayName,
          uid: currentUser()!.uid,
        },
      };

      if (itemToUnlinkCollection) {
        itemToUnlinkCollection.addItems(updatedItemToUnlink);
      }

      await Promise.all([
        updateDocument(this.localContent.host!.firestoreCollection!, this.localContent.id as string, {
          linkedDocuments: iwItem.linkedDocuments,
          editedOn: updatedHostItem.editedOn,
        }),
        updateDocument(this.config.firestoreCollection, id, {
          linkedDocuments: itemToUnlink.linkedDocuments,
          editedOn: updatedItemToUnlink.editedOn,
        }),
      ]);

      this.config.onUnlink?.(id);

      this.setSnack({
        message: 'Linked document was removed',
        action: {
          text: 'Undo',
          callback: () => {
            this.onLinkSelect({objectID: id});
            this.config.onUndoUnlink?.(id);
          },
        },
      });
    },
    async handleLinkedDocumentClick(doc: LinkedDocumentToDisplay) {
      // If the document is a linked OM, we don't want to trigger the link/iw action
      if (doc.isLinkedOm) return;

      if (this.config.linkAction) {
        const item = getCollection(this.config.filterCollection).getItem(doc.id);
        this.config.linkAction(item, this.config.filterCollection);
      }

      if (!this.config.iwAction) return;
      const currentId = this.localContent.id;
      // If the linked document does not have an info window tab, create one
      const index = this.infoWindowContents.findIndex((iw: InfoWindowContent) => iw.id === doc.id);
      if (index === -1) {
        // The filter collection only holds slim markers; merge in the full document.
        const slimItem = getCollection(this.config.filterCollection).getItem(doc.id);
        const dbDoc = await getDocument(this.config.firestoreCollection, doc.id, true);
        const itemToLink = {...slimItem, ...dbDoc};
        await this.config.iwAction({
          subject: {
            ...itemToLink,
            firestoreCollection: this.config.firestoreCollection,
            filterCollection: this.config.filterCollection,
          },
          insertIndex: 0,
        });
        this.setSelectedTabIndex(0);
        // If the current tab is not intended to appear yet on the item it is linked to,
        // the tab should be explicitly closed
        if (this.hiddenLink) {
          this.closeInfoWindowTab(currentId);
        }
      } else {
        this.setSelectedTabIndex(index);
      }
    },

    /**
     * Collects linked documents for the current InfoWindowContent, readying them for display.
     */
    async collectLinkedDocuments() {
      // TODO: Rethink this. We need the item in the local collection, but we don't add new buildings until they've
      // been saved. If we want to be able to link an item to a new building, we first need to make an edit to the
      // new building, such as adding a name, then the building will be added to the local collection.
      if (this.localContent.id === NEW_BUILDING_ID) return;

      this.linkedDocuments = [];
      const iwItem = this.retrieveIWItem();
      if (!iwItem) return;

      const linkedDocuments: {collection: string; id: string}[] = iwItem.linkedDocuments ?? [];
      if (linkedDocuments.length === 0) return;

      // Filter collections hold slim records and the OM store holds Light OM Items — neither carries
      // the heavy fields rendered below — and a target's collection may not be loaded yet. Collect
      // the ids that need a full Firestore read and batch per collection.
      const items = new Map<string, {value?: any; collectionType?: string}>();
      const idsToHydrate = new Map<string, string[]>();
      for (const {id, collection} of linkedDocuments) {
        if (!this.config.isOmInfoWindow && collection.endsWith('-om')) continue;

        let item: {value?: any; collectionType?: string} | undefined;
        try {
          item = getItemFromCollection(collection, id);
        } catch (error) {
          // Target not in any local collection; Firestore is the only source.
        }
        if (item) {
          items.set(`${collection}/${id}`, item);
        }

        if (!item || (item.value && !item.value.displayFields)) {
          const list = idsToHydrate.get(collection) ?? [];
          if (!list.includes(id)) {
            list.push(id);
          }
          idsToHydrate.set(collection, list);
        }
      }

      if (idsToHydrate.size > 0) {
        await Promise.all(
          [...idsToHydrate.entries()].map(async ([collection, ids]) => {
            // queryCollectionByIds splices the ids array; pass a copy so the source map stays intact.
            const fullDocs: Array<Record<string, any> & {id: string}> =
              await queryCollectionByIds(collection, [...ids], true);
            for (const full of fullDocs) {
              const key = `${collection}/${full.id}`;
              const existing = items.get(key);
              items.set(key, existing
                ? {...existing, value: {...existing.value, ...full}}
                : {value: full, collectionType: 'filter'});
            }
          }),
        );
      }

      for (const {id, collection} of linkedDocuments) {
        if (!this.config.isOmInfoWindow && collection.endsWith('-om')) continue;
        const item = items.get(`${collection}/${id}`);
        if (!item || !item.value || item.value.deleted) continue;

        // Some documents have links to multiple sections from the same collection.
        // e.g., for Monday items, there may be items from two boards for the same building.
        if (item.value.parentSection && item.value.parentSection !== this.sectionTitle) continue;

        const rawDisplayFields: DisplayFieldOptions[] = item.value.displayFields ?? [];
        let displayFields: DisplayField[] = rawDisplayFields.map((field) => new DisplayField(field));

        if (this.config?.fields) {
          displayFields = displayFields.filter((field: DisplayField) => {
            return this.config.fields?.includes(field.label);
          });
        }

        let textColor;
        const status = item.value.status;
        if (status) {
          textColor = '#8B8000';
          if (status === 'approved') {
            textColor = '#2FA42F';
          }
        }

        const itemIcon = item.value.markerData?.icon;
        const itemIconUrl = itemIcon ? markerIconMap.get(itemIcon)?.url : undefined;

        this.linkedDocuments.push({
          id,
          collection,
          name: item.value.name,
          headerSuffix: this.config.getHeaderSuffix(displayFields),
          displayFields,
          isLinkedOm: item.collectionType === 'storeOm',
          textColor,
          iconUrl: itemIconUrl,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.delete-button {
  position: absolute;
  z-index: 10;
  right: -28px;
  top: 1px;
  width: 20px;
  height: 20px;
}
.spacer {
  margin-top: 5px;
  padding-bottom: 15px;
}
.section {
  width: 95%;
  display: flex;
  align-items: center;
  text-overflow: ellipsis;
  flex: 0 1 auto;
  &__left {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 3px;
    flex: 0 1 auto;
  }
  &__right {
    flex: 0 0 auto;
    margin-left: auto;
  }
  &__expanded {
    overflow: visible;
    white-space: normal;
    display: block;
    margin-right: 5px;
  }
  &__external-link {
    &:hover {
      color: main.$linkBlue;
    }
  }
}
.comp-groups-display-fields {
  :deep(.displayFieldsWrapper) {
    width: inherit;
    margin-left: -5px;
  }

  :deep(.displayFieldValue) {
    margin-left: -26px;
    max-width: 195px;
    margin-right: -3px;

    :deep(.date-row-date-picker) {
      width: 192px;
    }
  }

  :deep(.displayFieldLabel) {
    max-width: 160px;
    margin-right: 25px;

    :deep(.v-field__input) {
      margin-left: -2px;
    }
  }

  :deep(.displayLabelIcon) {
    right: 33px;
  }

  :deep(.new-field-value-wrapper) {
    margin-left: -3px;
    max-width: 195px;
  }

  :deep(.new-field-label-wrapper) {
    max-width: 180.5px;
    margin-right: 2px;
  }
}

.caret {
  flex: 0 0 5%;
  height: 22px;
  width: 20px;
  margin-left: 0;
  transform: scale(1.5) translateX(-6px) translateY(-1px);
  &.expanded {
    transform: rotate(180deg) scale(1.5) translateX(5px);
  }
}

// eslint-disable vue-scoped-css/no-unused-selector
.v-expansion-panel {
  margin-bottom: 2px;
  margin-top: 0;
  .v-expansion-panel-title {
    height: 22px;
    padding: 0;
    min-height: 22px;
    margin-top: 5px;
    :deep(.v-expansion-panel-title__overlay){
      opacity: 0;
    }
    :deep(.v-expansion-panel-title__icon) {
      transform: translateX(4px);
    }
    :deep(.v-field__field) {
      background-color: #ebebeb;

      &:hover {
        background-color: #ebebeb;
      }
    }
  }
  .v-expansion-panel-text {
    :deep(.v-expansion-panel-text__wrapper) {
      padding: 0px 6px 0px;
    }
  }
  &.comp-type-panel::after {
    opacity: 1;
  }
  &.comp-panel::after {
    border: none;
  }
  &.comp-panel {
    :deep(.v-expansion-panel-title__icon) {
      margin-right: 6px;
      display: none;
    }
  }
}

a {
  padding: 0;
  font-size: 13px;
  text-decoration: none;
  z-index: 12;
  font-family: main.$fontPrimary;
  &:hover {
    color: main.$npOrange;
    text-decoration: underline;
    cursor: pointer;
  }
}

.add-link-search__item-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.linked-doc-avatar {
  margin-left: -2px;
  margin-right: 4px;
  flex-shrink: 0;
}
</style>
